import React, { useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import { ExpandCollapseIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ExpandCollapseIcon/ExpandCollapseIcon';
import { Controller, useFormContext } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import Input from 'src/components/Input/Input';
import { Checkbox } from 'src/components/ui/checkbox';
import { API_VERSION } from 'src/constants/api-routes';
import type { ConfigurationFormFieldOption } from 'src/services/configurationService';
import { getApiVersion } from 'src/utils/apiVersion';

const STYLE = {
  title: 'Style',
  icon: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.545 5.59135C10.545 6.26645 10.2768 6.9139 9.79946 7.39126C9.3221 7.86863 8.67465 8.13681 7.99956 8.13681C7.32446 8.13681 6.67701 7.86863 6.19965 7.39126C5.72228 6.9139 5.4541 6.26645 5.4541 5.59135C5.4541 4.91626 5.72228 4.26881 6.19965 3.79144C6.67701 3.31408 7.32446 3.0459 7.99956 3.0459C8.67465 3.0459 9.3221 3.31408 9.79946 3.79144C10.2768 4.26881 10.545 4.91626 10.545 5.59135ZM9.27228 5.59135C9.27228 5.9289 9.13819 6.25262 8.89951 6.49131C8.66083 6.72999 8.3371 6.86408 7.99956 6.86408C7.66201 6.86408 7.33828 6.72999 7.0996 6.49131C6.86092 6.25262 6.72683 5.9289 6.72683 5.59135C6.72683 5.2538 6.86092 4.93008 7.0996 4.6914C7.33828 4.45272 7.66201 4.31863 7.99956 4.31863C8.3371 4.31863 8.66083 4.45272 8.89951 4.6914C9.13819 4.93008 9.27228 5.2538 9.27228 5.59135Z"
        fill="#05050D"
        stroke="#05050D"
        strokeWidth="0.3"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 0.5C4.13409 0.5 1 3.63409 1 7.5C1 11.3659 4.13409 14.5 8 14.5C11.8659 14.5 15 11.3659 15 7.5C15 3.63409 11.8659 0.5 8 0.5ZM2.27273 7.5C2.27273 8.83 2.72645 10.0544 3.48691 11.0267C4.0211 10.3255 4.7101 9.75727 5.50013 9.36628C6.29017 8.97529 7.15987 8.77216 8.04136 8.77273C8.91151 8.77171 9.77041 8.96942 10.5525 9.35078C11.3347 9.73214 12.0194 10.2871 12.5545 10.9733C13.1058 10.2501 13.4771 9.40605 13.6375 8.51092C13.7979 7.61579 13.7428 6.69534 13.4769 5.82572C13.2109 4.95609 12.7417 4.1623 12.108 3.51002C11.4744 2.85774 10.6945 2.36573 9.83297 2.07469C8.97142 1.78365 8.05294 1.70195 7.15355 1.83635C6.25415 1.97075 5.39969 2.31739 4.66085 2.84758C3.92202 3.37777 3.32006 4.07628 2.90478 4.88531C2.4895 5.69433 2.27284 6.59062 2.27273 7.5ZM8 13.2273C6.68521 13.2294 5.41009 12.7771 4.39055 11.9469C4.80088 11.3593 5.34712 10.8795 5.98278 10.5485C6.61843 10.2174 7.32466 10.0448 8.04136 10.0455C8.74911 10.0448 9.44679 10.2131 10.0764 10.5363C10.7061 10.8595 11.2495 11.3282 11.6616 11.9036C10.6342 12.7608 9.33805 13.2294 8 13.2273Z"
        fill="#05050D"
        stroke="#05050D"
        strokeWidth="0.3"
      />
    </svg>
  ),
  description: 'Choose whether your AI speaks in a friendly, professional, or custom brand voice.',
};

// Keyed by the field's option keys.
const STYLE_OPTIONS: Record<
  string,
  { id: number; title: string; description?: React.ReactNode; emoji?: boolean }
> = {
  casual: {
    id: 0,
    title: 'Casual 👋',
    description: 'Friendly, conversational, approachable.',
    emoji: true,
  },
  professional: {
    id: 1,
    title: 'Professional 💼',
    description: 'Clear, precise, and business-like.',
    emoji: false,
  },
  custom: {
    id: 2,
    title: 'Custom 🌟',
    description: 'Custom Description',
    emoji: true,
  },
};

export const StyleAccordion = ({
  accordionValue,
  setAccordionValue,
  disabled = false,
  options = [],
}: {
  // The open accordion item; the three step-5 accordions share it so one is open at a time.
  accordionValue: string;
  setAccordionValue: (value: string) => void;
  disabled?: boolean;
  options?: ConfigurationFormFieldOption[];
}) => {
  const { control, setValue, clearErrors, watch } = useFormContext();
  const customInputRef = useRef(null);
  const style = watch('pf_tone_of_voice_style');
  const description = watch('pf_tone_of_voice_style_custom');
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;

  useEffect(() => {
    if (style !== 'custom') {
      setValue('pf_tone_of_voice_style_custom', '');
      clearErrors('pf_tone_of_voice_style_custom');
    }
  }, [style]);

  return (
    <div className={'flex flex-col gap-3'}>
      <div className={'flex flex-col gap-1'}>
        <div className={'flex flex-row gap-2.5 items-center'}>
          {STYLE.icon}
          <p className="text-sm font-semibold text-ebony">{STYLE.title}</p>
        </div>
        <p className={'text-sm text-ebony_opacity_50 font-medium'}>{STYLE.description}</p>
      </div>
      <div>
        {style && (
          <Accordion
            value={accordionValue}
            onValueChange={setAccordionValue}
            type="single"
            className="w-full min-w-[455px] max-w-[455px]"
            collapsible={true}
            disabled={disabled}
          >
            <AccordionItem
              value="style-card"
              className={clsx(
                'group border border-[#B8C1C633] rounded-sm-5 bg-white transition-shadow shadow-none',
                !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
                accordionValue === 'style-card' &&
                  style === 'custom' &&
                  !description &&
                  '[&>div]:overflow-visible',
              )}
            >
              <AccordionTrigger hideDefaultIcon className="py-2.5 px-5 w-full">
                <div className="flex items-center justify-between w-full">
                  <div className={'flex flex-row gap-[7px] overflow-hidden'}>
                    <div className={'text-start overflow-hidden'}>
                      <p className={'text-[#7A6BE5] text-sm font-semibold'}>
                        {STYLE_OPTIONS[style]?.title || style?.value}
                      </p>
                      <p
                        className={clsx(
                          'text-ebony_opacity_30 text-sm font-medium overflow-hidden whitespace-nowrap text-ellipsis',
                        )}
                      >
                        {style === 'custom'
                          ? description || STYLE_OPTIONS[style].description
                          : STYLE_OPTIONS[style].description}
                      </p>
                    </div>
                  </div>
                  {!disabled && (
                    <ExpandCollapseIcon
                      className={clsx(
                        'w-[12px] min-w-[12px] opacity-50 rotate-0 transition group-hover:opacity-100',
                        accordionValue === 'style-card' && 'rotate-180',
                      )}
                    />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent
                className={clsx('px-5 bg-light pb-2.5', style === 'custom' && 'overflow-visible')}
              >
                <div className={'flex flex-col'}>
                  <Controller
                    control={control}
                    name="pf_tone_of_voice_style"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroup
                        onValueChange={(val) => {
                          onChange(val);

                          if (val === 'custom') {
                            customInputRef?.current?.focus();
                          }
                        }}
                        value={value}
                        className="flex flex-col mt-5 mb-2.5 gap-5"
                      >
                        {options?.map((option) => {
                          const item = {
                            ...STYLE_OPTIONS[option.key],
                            ...option,
                          };

                          if (isV2 && option.value === 'custom') {
                            return null;
                          }

                          return (
                            <div key={`${item.id}-${item.key}`} className={'flex flex-col gap-2.5'}>
                              <div className={'flex items-start gap-2.5'}>
                                <RadioGroupItem
                                  className={'mt-1'}
                                  value={item.value}
                                  id={item.value}
                                />
                                <label htmlFor={item.value} className="cursor-pointer">
                                  <p className="text-sm font-semibold text-ebony opacity-80 hover:opacity-100 transition-colors">
                                    {item.title || item.key}
                                  </p>
                                  {item.key !== 'custom' && item.description && (
                                    <p className={'text-sm text-ebony_opacity_50 font-medium'}>
                                      {item.description}
                                    </p>
                                  )}
                                </label>
                              </div>
                              {item.key === 'casual' && style === 'casual' && (
                                <Controller
                                  control={control}
                                  name="pf_tone_of_voice_emoji"
                                  render={({
                                    field: { onChange: onEmojiChange, value: emojiValue },
                                  }) => (
                                    <div className="flex items-center w-full">
                                      <label
                                        htmlFor={`sub-emoji`}
                                        className="pb-2.5 w-full text-xs flex items-center gap-2.5 text-ebony_opacity_30 cursor-pointer font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                      >
                                        <Checkbox
                                          id={`sub-emoji`}
                                          checked={emojiValue}
                                          onCheckedChange={onEmojiChange}
                                        />
                                        <p>Emoji usage</p>
                                      </label>
                                    </div>
                                  )}
                                />
                              )}
                            </div>
                          );
                        })}
                      </RadioGroup>
                    )}
                  />
                  {style === 'custom' && (
                    <Controller
                      control={control}
                      name={'pf_tone_of_voice_style_custom'}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <div className={'flex flex-col w-full relative'}>
                          <Input
                            ref={customInputRef}
                            readonly={style !== 'custom'}
                            textareaRows={4}
                            type={'textarea'}
                            autoFocus={true}
                            placeholder={
                              'Provide your AI agent’s role here. For example: Customer Support Specialist who assists users with troubleshooting.'
                            }
                            invalidMessage={error?.message}
                            value={value}
                            isValid={true}
                            onChange={onChange}
                          />
                          {error?.message && (
                            <div
                              className={
                                'absolute bg-white top-[30%] left-[calc(100%+8px)] border border-[#C11A45B2] text-[#C11A45B2] rounded-sm-5 text-xs px-3 py-[9px]'
                              }
                            >
                              <div className="absolute left-0 top-1/4 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-white border-l border-b border-[#C11A45B2]"></div>
                              {error?.message}
                            </div>
                          )}
                        </div>
                      )}
                    />
                  )}
                  {style === 'custom' && (
                    <Controller
                      control={control}
                      name="pf_tone_of_voice_emoji"
                      render={({ field: { onChange: onEmojiChange, value: emojiValue } }) => (
                        <div className="flex items-center w-full mt-2.5">
                          <label
                            htmlFor={`sub-emoji`}
                            className="pb-2.5 w-full text-xs flex items-center gap-2.5 text-ebony_opacity_30 cursor-pointer font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            <Checkbox
                              id={`sub-emoji`}
                              checked={emojiValue}
                              onCheckedChange={onEmojiChange}
                            />
                            <p>Emoji usage</p>
                          </label>
                        </div>
                      )}
                    />
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    </div>
  );
};
